import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/checkly')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Checkly Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Checkly: Checkly is built for monitoring as code with Playwright. Uptrack checks 50 monitors every 30s for $3/mo." },
      { property: 'og:title', content: 'Checkly Alternative — Uptrack' },
      { property: 'og:description', content: "Checkly Starter is $24/mo with per-run overage. Uptrack Pro is $3/mo for 50 monitors at 30s, with flat pricing." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-checkly.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/checkly' },
    ],
    scripts: comparisonScripts('checkly', "Checkly"),
  }),
})

const data: ComparisonData = {
  competitor: "Checkly",
  tagline: "Checkly is built for engineering teams: Playwright browser checks, API checks, and monitoring as code in CI/CD. Uptrack is a simpler option for uptime and basic browser flows, from $3/mo.",
  heroDescription: "These facts come from Checkly's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "Hobby: 10 uptime monitors at 2 min" },
    { feature: "Cheapest paid plan", uptrack: "Pro $3/mo: 50 monitors at 30s", competitor: "Starter $24/mo: 50 uptime monitors at 1 min", uptrackWins: true },
    { feature: "30-second checks", uptrack: "Every plan, from $3/mo", competitor: "Team plan, $64/mo", uptrackWins: true },
    { feature: "Overage", uptrack: "None (flat plans)", competitor: "API and browser runs above plan limits billed per run", uptrackWins: true },
    { feature: "Status pages (cheapest paid plan)", uptrack: "5 on Pro", competitor: "1 on Starter", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "1 sec (Enterprise)" },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks", competitor: "Configurable retries (fixed, linear, exponential)" },
    { feature: "Browser checks", uptrack: "Playwright + per-run console/network", competitor: "Playwright-based" },
    { feature: "Monitoring-as-code", uptrack: "Yes — CLI (apply/export, YAML)", competitor: "Full CLI + Git workflow" },
    { feature: "CI/CD integration", uptrack: "CLI runs in any CI", competitor: "GitHub, GitLab, Vercel" },
  ],

  painPoints: [
    {
      title: "Price for uptime monitoring",
      description: "Checkly Starter is $24/mo for 50 uptime monitors at 1-minute checks. Uptrack Pro is $3/mo for 50 monitors at 30-second checks.",
    },
    {
      title: "Usage-based overage",
      description: "Checkly plans include a set number of API and browser check runs. Runs above that cost $2.60 per 10,000 API runs and $6.50 per 1,000 browser runs. Uptrack has no overage: browser checks run at a fixed frequency per plan.",
    },
    {
      title: "30-second checks start on Team",
      description: "Checkly's Team plan ($64/mo) is the first with 30-second checks. Every Uptrack plan checks every 30 seconds.",
    },
  ],

  differentiators: [
    "30-second checks on every plan, from $3/mo for 50 monitors.",
    "Flat pricing with no overage. Browser check frequency is fixed per plan, so the bill does not grow with runs.",
    "Status pages on every plan: 5 on Pro, unlimited on Team and Business.",
    "Browser runs show per-run console logs (errors highlighted) and network requests (4xx/5xx flagged), so you see what broke, not only that it broke.",
    "Alert confirmation with 2-5 consecutive failed checks to reduce false positives.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you want browser checks written as Playwright test code that lives next to your application code, Checkly is built for that.",
    "If you want browser checks authored as code with Checkly's mature constructs. Uptrack has a simpler YAML CLI (apply/export), not a code-first checks framework.",
    "If you need check intervals under 30 seconds, Checkly Enterprise checks as often as every second.",
    "If you need deep CI/CD integration with GitHub Actions, GitLab CI, or Vercel deployments.",
    "If you need more than 5 free monitors, Checkly Hobby includes 10 uptime monitors plus a monthly allowance of API and browser runs. Uptrack's free plan is 5 HTTP and keyword monitors at 30 seconds from one location.",
  ],
}
